import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { fetchV2Applications, setFetchLoading, fetchApplication, postApplication, safePdf } from "../../../store/reducers/applicationSlice";
import { getallUser } from "../../../store/reducers/userSlice";
import { getAttachments } from "../../../store/reducers/fileSlice";
import { createNotification } from '../../../store/reducers/notificationSlice';
import { showSuccess, showError } from '../../../helpers/toast';
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const AppRenewals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchTerm("");
  };

  // debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      let term = searchTerm;
      if (searchBy === "applicationId" && term.startsWith("GB-")) {
        term = term.replace("GB-", "");
      }
      setDebouncedSearchTerm(term);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, searchBy]);
  const [loading, setLoading] = useState(true);

  // Fetch all users
  useEffect(() => {
    // SECURITY: Add company_id/user_id filter based on user role
    let queryParams = {};
    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    } else if (user.user?.user_role === "employee" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    }

    dispatch(getallUser({
      authToken: user.access_token,
      queryParams: queryParams
    }));
  }, [dispatch, user.access_token, user.user?.user_role, user.user?.company_id]);

  const [applicationsData, setApplicationsData] = useState([]);

  // Fetch Renewals Data
  // Fetch Renewals Data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      dispatch(setFetchLoading(true)); // Set global loading state
      let queryParams = {
        accessToken: authToken,
        state: "approved",
        dueInDays: 10,
        sortOrder: "DESC",
        sortField: "id",
      };

      // SECURITY: Add company_id filter at API level for company_admin
      if (user.user?.user_role === "company_admin" && user.user?.company_id) {
        queryParams.company_id = user.user.company_id;
      }
      // SECURITY: Add user_id filter at API level for employees
      else if (user.user?.user_role === "employee" && user.user?.id && !debouncedSearchTerm) {
        queryParams.userId = user.user.id;
      }
      // Fallback: if no role, use user.id (backward compatibility)
      else if (user.user?.id && !debouncedSearchTerm) {
        queryParams.userId = user.user.id;
      }

      if (startDate) {
        queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
      }
      if (debouncedSearchTerm) {
        queryParams[searchBy] = debouncedSearchTerm;
      }

      try {
        const response = await fetchV2Applications(queryParams);
        const applications = response?.applications || [];

        // SECURITY: Additional frontend filtering by role and company
        const userRole = user.user?.user_role;
        const currentUserId = user.user?.id;
        const userCompanyId = user.user?.company_id;

        const filteredByRole = applications.filter((item) => {
          // If server search is active, trust the API results
          if (debouncedSearchTerm) return true;

          const itemCompanyId = item.company_id || item.companyId;
          const itemUserId = item.user_id || item.userId || item.user?.id;

          if (userRole === "admin" || userRole === "super_admin") {
            return true;
          } else if (userRole === "company_admin") {
            // Company admin can see all applications in their company
            return String(itemCompanyId) === String(userCompanyId);
          } else if (userRole === "employee") {
            // Employee can only see their own applications within their company
            const isOwnApplication = String(itemUserId) === String(currentUserId);
            const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
            return isOwnApplication && hasCorrectCompany;
          } else {
            // Default: filter by company (fallback)
            return String(itemCompanyId) === String(userCompanyId);
          }
        });

        setApplicationsData(filteredByRole);
      } catch (error) {
        console.error("Error fetching applications:", error);
      } finally {
        setLoading(false);
        dispatch(setFetchLoading(false)); // Clear global loading state
      }
    };

    fetchData();
  }, [authToken, startDate, endDate, user.user?.id, user.user?.user_role, user.user?.company_id, dispatch, debouncedSearchTerm, searchBy]);

  // Merge user data with application data using useMemo
  useEffect(() => {
    if (applicationsData?.length > 0 && getallUserdata?.length > 0) {
      // Create a Map for O(1) user lookup instead of O(n) find()
      const userMap = new Map(getallUserdata.map(user => [user.id, user]));

      // Merge data efficiently
      const mergedData = applicationsData.map((appItem) => {
        // Correctly get user ID from application
        const userIdFromApp =
          appItem.user_id ||
          appItem.userId ||
          appItem.user?.id;

        const userItem = userMap.get(userIdFromApp);

        // Correctly extract application ID (THIS WAS THE PROBLEM)
        const applicationId =
          appItem.id ||
          appItem.applicationId ||
          appItem.application_id;

        return {
          applicationId,       // <-- FINAL CORRECT APPLICATION ID
          ...appItem,          // application data

          ...(userItem || {}), // merged user data

          userId: userItem?.id || userIdFromApp, // ensure correct user id
        };
      });

      setFilteredApplication(mergedData);
    } else if (applicationsData?.length > 0) {
      // If user data not loaded yet, set applications without merge
      setFilteredApplication(applicationsData);
    } else {
      setFilteredApplication([]); // Clear data if no applications found
    }
  }, [applicationsData, getallUserdata]);

  // Search filter
  const searchedData = useMemo(() => {
    return filteredApplication.filter((item) => {

      if (debouncedSearchTerm && (searchTerm || searchQuery)) return true;
      if (!searchQuery && !searchTerm) return true; // Show all if no search query

      const term = searchQuery || searchTerm;
      return Object.values(item).some((val) =>
        val &&
        val.toString().toLowerCase().includes(term.toLowerCase())
      );
    });
  }, [filteredApplication, searchQuery, searchTerm, debouncedSearchTerm]);

  // Reset to page 1 only when search query or date filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchTerm, startDate, endDate]);

  // Pagination data
  const paginatedData = useMemo(() => {
    return searchedData
      .sort((a, b) => b.id - a.id)
      .slice((currentPage - 1) * 10, currentPage * 10);
  }, [searchedData, currentPage]);

  const getTotalPages = () => Math.ceil(searchedData.length / 10);

  // Download
  const handleDownload = useCallback(async (item) => {
    const applicationId = item.applicationId || item.id || item.application_id;
    const userId = item.userId || item.user_id || item.user?.id;

    if (!applicationId) {
      showError('Application ID is missing');
      return;
    }

    if (!userId) {
      showError('User ID is missing');
      return;
    }

    // Prevent multiple clicks
    if (downloadingIds.includes(applicationId)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/zip",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `application_${applicationId}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Files downloaded successfully!');
    } catch (error) {
      console.error("Download failed", error);
      showError(`Download failed: ${error.message}`);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  }, [authToken, downloadingIds]);

  // Create new application from previous data
  const handleCreateNewApplication = useCallback(async (row) => {
    try {
      const applicationId = row.applicationId;  // <-- use this, not row.id
      const userId = row.userId || row.user_id || user.user?.id;

      showSuccess('Fetching previous application data...');

      const applicationResponse = await dispatch(fetchApplication({
        id: applicationId,
        accessToken: authToken
      }));

      if (!applicationResponse.payload) {
        showError('Failed to fetch previous application data');
        return;
      }

      const previousApplication =
        applicationResponse.payload.application ||
        applicationResponse.payload.applications?.[0] ||
        applicationResponse.payload;

      if (!previousApplication) {
        showError('Application data not found');
        return;
      }

      let jsonData = previousApplication.json_data;

      if (typeof jsonData === "string") {
        try {
          jsonData = JSON.parse(jsonData);
        } catch {
          jsonData = {};
        }
      }

      // Get attachment file names to pass to backend for copying
      let attachmentFiles = [];
      try {
        const attachments = await getAttachments({
          userId,
          applicationId,
          authToken
        });

        if (Array.isArray(attachments) && attachments.length > 0) {
          // Pass file names as objects - backend will copy them from source application
          attachmentFiles = attachments.map((name) => ({ name }));
        } else {
        }
      } catch (e) {
        console.error("Attachment fetch failed", e);
      }

      showSuccess("Creating new application...");

      const newApplicationResponse = await dispatch(postApplication({
        form: previousApplication.name || previousApplication.form,
        json_data: jsonData,
        name: previousApplication.name,
        category: previousApplication.category || previousApplication.categoryName,
        user_id: user.user.id,
        token: authToken,
        state: "draft",
        files: attachmentFiles, // File names - backend will copy from sourceApplicationId
        sourceApplicationId: applicationId, // Backend will copy files from this application
        sourceUserId: userId // Source user ID for file copying
      }));

      if (newApplicationResponse.payload?.id) {
        const newApplicationId = newApplicationResponse.payload.id;

        await dispatch(safePdf({
          htmlForm: previousApplication.htmlForm || previousApplication.html_form || "<div id=\"DocumentPDF\"></div>",
          user: { id: user.user.id },
          id: newApplicationId,
          token: authToken
        }));

        dispatch(createNotification({
          user_id: user.user.id,
          applicationId: newApplicationId,
          applicationName: previousApplication.name,
          status: "drafts",
          accessToken: authToken,
          sendToCompanyAdmins: true
        }));

        navigate(`/app-renewal/${newApplicationId}`);
      } else {
        showError("Failed to create new application");
      }

    } catch (error) {
      console.error("Error creating new application:", error);
      showError(error.message);
    }
  }, [dispatch, authToken, user, navigate]);

  
  // Table Columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * 10 + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => {
          const appId = info.getValue();
          return appId ? `GB-${appId}` : "N/A";
        },
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name}>
                {row.name?.length > 50 ? `${row.name.substring(0, 50)}...` : row.name}
              </strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;

          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || row.name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("due_date", {
        header: "Due Date",
        cell: (info) => <span>{info.getValue()?.slice(0, 10)}</span>,
      }),
      columnHelper.accessor("issue_date", {
        header: "Registered Date",
        cell: (info) => <span>{info.getValue()?.slice(0, 10)}</span>,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      onClick={() => {
                        const applicationId = row.applicationId || row.id;
                        if (!downloadingIds.includes(applicationId)) {
                          handleDownload(row);
                        }
                      }}
                      className="dropdown-item"
                      style={{
                        opacity: downloadingIds.includes(row.applicationId || row.id) ? 0.6 : 1,
                        pointerEvents: downloadingIds.includes(row.applicationId || row.id) ? 'none' : 'auto',
                        cursor: downloadingIds.includes(row.applicationId || row.id) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {downloadingIds.includes(row.applicationId || row.id) ? 'Downloading...' : 'Download'}
                    </Link>
                  </li>
                  {/* <li>
                    <Link
                      onClick={() => navigate(`/app-renewal/${row.applicationId || row.id}`)}
                      className="dropdown-item"
                    >
                      View
                    </Link>
                  </li> */}
                  <li>
                    <Link
                      onClick={(e) => {
                        e.preventDefault();
                        handleCreateNewApplication(row);
                      }}
                      className="dropdown-item"
                    >
                      Create new appllication
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, handleDownload, navigate, getallUserdata, handleCreateNewApplication, currentPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header    />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Renewals</h2>
            </div>
            <div className="boardHeadRight">
              <select onChange={handleSearchByChange} value={searchBy} >
                <option value="applicationId">Search by Application ID</option>
                <option value="username">Search by Employee Name</option>

              </select>
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => {
                    let val = e.target.value;
                    if (searchBy === "applicationId") {
                      const digits = val.replace(/\D/g, "");
                      if (digits.length > 0) {
                        val = `GB-${digits}`;
                      } else {
                        val = "";
                      }
                    }
                    setSearchTerm(val);
                  }}
                  value={searchTerm}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>

          {/* Table Head */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}

                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr className="" key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}

                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default AppRenewals;
